/**
 * Extracts error information from API responses that may return HTTP 200
 * but contain an internal error (status_code/statusCode 4xx/5xx) in the body.
 *
 * @param {Object} responseData - The full response body from the API (parsed JSON)
 * @param {string} [defaultErrorMessage='An error occurred.'] - Fallback message when none found
 * @returns {Object|null} If error: { message, statusCode, responseData }; if success: null
 */
export const extractApiError = (
  responseData,
  defaultErrorMessage = 'An error occurred.',
) => {
  if (!responseData) {
    return { message: 'Invalid response from server', statusCode: 500, responseData: null };
  }

  const statusCode = responseData.status_code ?? responseData.statusCode ?? 200;
  const msgCode = responseData.msg_code ?? responseData.msgCode ?? [];

  if (statusCode >= 200 && statusCode < 300) return null;

  const errorMessage =
    msgCode.length > 0
      ? (msgCode[0].description ?? msgCode[0].code ?? defaultErrorMessage)
      : defaultErrorMessage;

  return { message: errorMessage, statusCode, responseData };
};

/**
 * Validates an API response and either returns a normalized object or throws an Error.
 *
 * @param {Object} responseData - The full response body from the API
 * @param {string} [defaultErrorMessage='An error occurred.'] - Fallback message
 * @returns {Object} Normalized response: { status_code, msg_code, body, meta, request_id }
 * @throws {Error} When internal status_code is not 2xx
 */
export const validateApiResponse = (
  responseData,
  defaultErrorMessage = 'An error occurred.',
) => {
  const error = extractApiError(responseData, defaultErrorMessage);
  if (error) {
    const apiError = new Error(error.message);
    apiError.response = { data: error.responseData, status: error.statusCode };
    throw apiError;
  }

  return {
    status_code: responseData.status_code ?? responseData.statusCode ?? 200,
    msg_code: responseData.msg_code ?? responseData.msgCode ?? [],
    body: responseData.body ?? null,
    meta: responseData.meta ?? null,
    request_id: responseData.request_id ?? responseData.requestId ?? null,
  };
};

/**
 * Parses the agents API envelope, which differs from the workspaces envelope:
 *   - Data lives in `data` (not `body`)
 *   - No numeric `status_code`; errors surface as HTTP-level failures or via `msgCode`
 *   - `meta` uses camelCase keys (handled by normalizePagedMeta)
 *   - `requestId` (camelCase)
 *
 * Returns `{ rows, meta, requestId }` or throws an Error.
 *
 * @param {Object} responseData
 * @param {string} [defaultErrorMessage]
 * @returns {{ rows: unknown[], meta: Object | null, requestId: string | null }}
 * @throws {Error}
 */
/**
 * Parses GET /v1/workspace/agent/:id response.
 *
 * Actual envelope (standard platform format):
 *   {
 *     statusCode: 200,
 *     requestId: "...",
 *     msgCode: [{ code, description }],
 *     body: { uuid, name, type, category, description, status, starters, ... }
 *   }
 *
 * Returns the `body` object or throws on error.
 *
 * @param {Object} responseData
 * @param {string} [defaultErrorMessage]
 * @returns {Record<string, unknown>}
 * @throws {Error}
 */
export const parseAgentDetailResponse = (
  responseData,
  defaultErrorMessage = 'An error occurred.',
) => {
  if (!responseData || typeof responseData !== 'object') {
    throw new Error(defaultErrorMessage);
  }

  const statusCode = responseData.status_code ?? responseData.statusCode;
  if (statusCode != null && statusCode >= 400) {
    const msgCode = responseData.msg_code ?? responseData.msgCode ?? [];
    const message =
      msgCode.length > 0
        ? (msgCode[0].description ?? msgCode[0].code ?? defaultErrorMessage)
        : defaultErrorMessage;
    const apiError = new Error(message);
    apiError.response = { data: responseData, status: statusCode };
    throw apiError;
  }

  // Detail endpoint puts the agent record in `body`
  const body = responseData.body ?? responseData.data ?? null;
  if (!body || typeof body !== 'object' || Array.isArray(body)) {
    throw new Error(defaultErrorMessage);
  }

  return body;
};

/**
 * True when GET agent instruction returned 404 with known msgCode (empty state, not an error UI).
 *
 * @param {unknown} err  Axios-like error
 * @returns {boolean}
 */
export const isAgentInstructionNotFoundError = (err) => {
  const status =
    err?.response?.status ?? err?.status ?? err?.raw?.response?.status ?? err?.raw?.status;
  if (status !== 404) return false;
  const data = err?.response?.data ?? err?.raw?.response?.data ?? err?.data;
  const msgCode = data?.msgCode ?? data?.msg_code ?? [];
  if (!Array.isArray(msgCode)) return false;
  return msgCode.some((m) => m?.code === 'agent_instruction_not_found');
};

/**
 * Normalizes instruction payload from GET /v1/workspace/agent/:id/instruction `body`
 * (or a raw body-shaped object).
 *
 * @param {Record<string, unknown>|null|undefined} data
 * @returns {{ instruction: string, instructionUuid: string | null }}
 */
export const parseAgentInstructionBody = (data) => {
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    return { instruction: '', instructionUuid: null };
  }
  const instruction = typeof data.instruction === 'string' ? data.instruction : '';
  const raw = data.instructionUuid ?? data.instruction_uuid ?? null;
  const instructionUuid = typeof raw === 'string' && raw.trim() ? raw.trim() : null;
  return { instruction, instructionUuid };
};

/**
 * Extracts new instruction UUID from POST /v1/instruction/create response
 * (ANN-style: response.data.data.instructionUuid — here `data` is already axios response body).
 *
 * @param {Record<string, unknown>|null|undefined} responseData
 * @returns {string|null}
 */
export const parseInstructionCreateUuid = (responseData) => {
  if (!responseData || typeof responseData !== 'object') return null;
  const layer = responseData.data ?? responseData.body ?? responseData;
  const inner = layer && typeof layer === 'object' && !Array.isArray(layer) ? layer.data ?? layer : null;
  if (!inner || typeof inner !== 'object') return null;
  const u = inner.instructionUuid ?? inner.instruction_uuid ?? inner.uuid;
  return typeof u === 'string' && u.trim() ? u.trim() : null;
};

/**
 * Parses POST /v1/workspace/agent/create response.
 * Supports both known envelopes:
 *   - { body: { ...agent } }
 *   - { data: { body: { ...agent } } }
 *
 * @param {Object} responseData
 * @param {string} [defaultErrorMessage]
 * @returns {Record<string, unknown>}
 */
export const parseAgentCreateResponse = (
  responseData,
  defaultErrorMessage = 'An error occurred.',
) => {
  if (!responseData || typeof responseData !== 'object') {
    throw new Error(defaultErrorMessage);
  }

  const statusCode = responseData.status_code ?? responseData.statusCode;
  if (statusCode != null && statusCode >= 400) {
    const msgCode = responseData.msg_code ?? responseData.msgCode ?? [];
    const message =
      msgCode.length > 0
        ? (msgCode[0].description ?? msgCode[0].code ?? defaultErrorMessage)
        : defaultErrorMessage;
    const apiError = new Error(message);
    apiError.response = { data: responseData, status: statusCode };
    throw apiError;
  }

  const body = responseData.body ?? responseData.data?.body ?? null;
  if (!body || typeof body !== 'object' || Array.isArray(body)) {
    throw new Error(defaultErrorMessage);
  }
  return body;
};

/**
 * Parses POST /v1/workspace/agent/update response.
 * Returns the normalized body when present.
 *
 * @param {Object} responseData
 * @param {string} [defaultErrorMessage]
 * @returns {Record<string, unknown> | null}
 */
export const parseAgentUpdateResponse = (
  responseData,
  defaultErrorMessage = 'An error occurred.',
) => {
  if (!responseData || typeof responseData !== 'object') {
    throw new Error(defaultErrorMessage);
  }

  const statusCode = responseData.status_code ?? responseData.statusCode;
  if (statusCode != null && statusCode >= 400) {
    const msgCode = responseData.msg_code ?? responseData.msgCode ?? [];
    const message =
      msgCode.length > 0
        ? (msgCode[0].description ?? msgCode[0].code ?? defaultErrorMessage)
        : defaultErrorMessage;
    const apiError = new Error(message);
    apiError.response = { data: responseData, status: statusCode };
    throw apiError;
  }

  const body = responseData.body ?? responseData.data?.body ?? responseData.data ?? null;
  if (body != null && (typeof body !== 'object' || Array.isArray(body))) {
    throw new Error(defaultErrorMessage);
  }
  return body;
};

export const parseAgentListResponse = (
  responseData,
  defaultErrorMessage = 'An error occurred.',
) => {
  if (!responseData || typeof responseData !== 'object') {
    throw new Error(defaultErrorMessage);
  }

  // Explicit numeric error status (some endpoints still include it)
  const statusCode = responseData.status_code ?? responseData.statusCode;
  if (statusCode != null && statusCode >= 400) {
    const msgCode = responseData.msg_code ?? responseData.msgCode ?? [];
    const message =
      msgCode.length > 0
        ? (msgCode[0].description ?? msgCode[0].code ?? defaultErrorMessage)
        : defaultErrorMessage;
    const apiError = new Error(message);
    apiError.response = { data: responseData, status: statusCode };
    throw apiError;
  }

  const rows = Array.isArray(responseData.data) ? responseData.data : [];
  const meta = responseData.meta ?? null;
  const requestId = responseData.requestId ?? responseData.request_id ?? null;

  return { rows, meta, requestId };
};

/**
 * POST /v1/tenant/file/create — pulls out the presigned upload URL and the new
 * file's uuid.
 *
 * The endpoint nests the record one level deeper than the sibling fields:
 *
 *   body: { msgCode, requestId, file: { uuid, id, name, path, bucket, … },
 *           uploadUrl, fileKey }
 *
 * so `uploadUrl` sits at the top of `body` while the id is `body.file.uuid` —
 * NOT `body.file_uuid`. Reading only the flat keys made the upload throw
 * "Failed to get file UUID" and roll the document back on a 200 response
 * (CT-5304, visible only once the identity fix let the flow reach this step).
 * The flat and snake_case spellings are kept as fallbacks so an environment
 * still on the older shape keeps working.
 *
 * @param {Record<string, unknown>|null|undefined} responseData  Full envelope or a body-shaped object
 * @returns {{ uploadUrl: string|null, fileUuid: string|null }}
 */
export const parseFileCreateResponse = (responseData) => {
  if (!responseData || typeof responseData !== 'object') {
    return { uploadUrl: null, fileUuid: null };
  }

  const body = responseData.body ?? responseData;
  if (!body || typeof body !== 'object') return { uploadUrl: null, fileUuid: null };

  const file = body.file && typeof body.file === 'object' ? body.file : {};

  const pick = (...candidates) =>
    candidates.find((v) => typeof v === 'string' && v.trim()) ?? null;

  return {
    uploadUrl: pick(body.uploadUrl, body.upload_url, file.uploadUrl, file.upload_url),
    fileUuid: pick(file.uuid, file.file_uuid, file.fileUuid, body.file_uuid, body.fileUuid),
  };
};

/**
 * GET /v1/workspace/agent/:id/drives/list — response envelope varies (`body.data`, `data`, array).
 *
 * @param {Record<string, unknown>|null|undefined} res
 * @returns {unknown[]}
 */
export const parseAgentDrivesListFromResponse = (res) => {
  if (!res || typeof res !== 'object') return [];
  if (Array.isArray(res.data)) return res.data;
  const body = res.body ?? res;
  const inner = body.data ?? body.rows ?? body.items;
  if (Array.isArray(inner)) return inner;
  if (Array.isArray(body)) return body;
  return [];
};

/**
 * GET /v1/workspace/agent/:id/documents/list
 *
 * @param {Record<string, unknown>|null|undefined} res
 * @returns {unknown[]}
 */
export const parseAgentDocumentsListFromResponse = (res) => {
  if (!res || typeof res !== 'object') return [];
  if (Array.isArray(res.data)) return res.data;
  if (Array.isArray(res.body?.data)) return res.body.data;
  if (Array.isArray(res.body)) return res.body;
  return [];
};

/**
 * POST /v1/tenant/document/list — unify `body` vs `data` shapes.
 *
 * @param {Record<string, unknown>|null|undefined} res
 * @returns {unknown[]}
 */
export const parseTenantDocumentListFromResponse = (res) => {
  if (!res || typeof res !== 'object') return [];
  if (Array.isArray(res.body)) return res.body;
  if (Array.isArray(res.data?.body)) return res.data.body;
  if (Array.isArray(res.data)) return res.data;
  return [];
};
